import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getTeamMembers,
  getTeamNewsfeed,
} from 'services/resources/commonResources';
import {
  useCreateOrderMutation,
  useGenerateUploadLinkMutation,
  useGetOrdersHistoryQuery,
  useGetOrdersQuery,
} from 'store/apis/orderApi';
import {
  sorterForChangelog,
  splitOrders,
} from 'components/OrdersList/utilities/helpers';
import { useQueryError } from 'utilities/hooks/useQueryError/useQueryError';
import {
  FAIL_GENERATE_UPLOAD_LINK,
  FETCH_ORDER_HISTORY_ERROR_MESSAGE,
  FETCH_ORDERS_ERROR_MESSAGE,
} from 'constants/errorMessages';
import {
  filterOrdersByTeam,
  getOrderParamsForUpload,
  getSelectedHistoryTabs,
  getSelectedOrders,
  ordersSorter,
} from 'components/UserProfile/utilities';
import { sortArray } from 'utilities/general';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { UNABLE_CREATE_ORDER_MESSAGE } from 'components/OrderForm/Info/constants';
import {
  IF_SLIDES_COUNT_ATTRIBUTE,
  REQUESTED_H_AND_E_ATTRIBUTE,
  REQUESTED_IMAGE_ANALYSIS_ATTRIBUTE,
  REQUESTED_PATHOLOGY_CONSULTATION_ATTRIBUTE,
  REQUESTED_SPECIAL_STAINS_ATTRIBUTE,
  SLIDES_COUNT_ATTRIBUTE,
} from 'utilities/constants';
import { useLazyGetAllAnnotationsPaginatedQuery } from 'store/apis/annotationsApi';
import { removeDuplicatesByUuid } from 'utilities/array';

// A team's comment feed runs to hundreds of rows; the communication card asks for
// one page at a time so the first paint is not gated on the whole history.
export const NEWSFEED_SLIDE_COMMENTS_PAGE_SIZE = 25;

export const useTeamNewsFeed = (selectedTeam) => {
  const [newsfeeds, setNewsfeeds] = useState(new Map());
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    if (!selectedTeam || !!newsfeeds.get(selectedTeam)) return;

    getTeamNewsfeed(selectedTeam.uuid, {
      limit: NEWSFEED_SLIDE_COMMENTS_PAGE_SIZE,
    }).then((newsfeed) => {
      setNewsfeeds((prevNewsfeeds) => {
        prevNewsfeeds.set(selectedTeam, newsfeed);

        return new Map(prevNewsfeeds);
      });
    });
  }, [selectedTeam]);

  const loadMoreSlideComments = useCallback(() => {
    const newsfeed = newsfeeds.get(selectedTeam);

    if (!selectedTeam || !newsfeed) return;

    setIsLoadingMore(true);

    return getTeamNewsfeed(selectedTeam.uuid, {
      limit: NEWSFEED_SLIDE_COMMENTS_PAGE_SIZE,
      offset: newsfeed.slide_comments.length,
    })
      .then((nextPage) => {
        setNewsfeeds((prevNewsfeeds) => {
          const current = prevNewsfeeds.get(selectedTeam);

          prevNewsfeeds.set(selectedTeam, {
            ...current,
            slide_comments: [
              ...current.slide_comments,
              ...nextPage.slide_comments,
            ],
            slide_comments_count: nextPage.slide_comments_count,
          });

          return new Map(prevNewsfeeds);
        });
      })
      .finally(() => setIsLoadingMore(false));
  }, [selectedTeam, newsfeeds]);

  return {
    newsfeeds,
    loadMoreSlideComments,
    isLoadingMore,
  };
};

export const useGenerateUploadLink = (selectedOrder, setIsLoading) => {
  const [uploadLink, setUploadLink] = useState(
    selectedOrder?.files_com_share_url,
  );
  const [generateLink] = useGenerateUploadLinkMutation();
  const [createOrderMutation] = useCreateOrderMutation();

  const { showError } = useSnackbar();

  const generateUploadLink = (selectedOrderValue) => {
    setIsLoading(true);
    generateLink({ orderUUID: selectedOrderValue.uuid })
      .unwrap()
      .then(({ files_com_share_url }) => {
        setUploadLink(files_com_share_url);
      })
      .catch(() => {
        showError(FAIL_GENERATE_UPLOAD_LINK);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePrepareUpload = (
    selectedOrderValue,
    selectedProject,
    selectedTeam,
    setSelectedOrder,
  ) => {
    if (selectedProject.uuid && selectedOrderValue) {
      generateUploadLink(selectedOrderValue);

      return;
    }
    setIsLoading(true);
    const postParams = getOrderParamsForUpload(selectedTeam.uuid);
    if (selectedProject.uuid) {
      postParams.project_uuid = selectedProject.uuid;
      delete postParams.team_uuid;
    } else {
      postParams.create_project_name = selectedProject.name;
    }
    createOrderMutation(postParams)
      .unwrap()
      .then((createdOrder) => {
        setSelectedOrder(createdOrder);
        generateUploadLink(createdOrder);
      })
      .catch(() => {
        showError(UNABLE_CREATE_ORDER_MESSAGE);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return {
    uploadLink,
    handlePrepareUpload,
  };
};

export const useTeamMembers = (team) => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!team) return;

    getTeamMembers(team.uuid)
      .then((res) => {
        setMembers(res);
      })
      .catch()
      .finally(() => setIsLoading(false));
  }, [team]);

  return {
    members,
    isLoading,
  };
};

export const usePrepareOrderForSelectedTab = (team, tab, sort) => {
  const {
    data: rawOrders,
    isLoading: isOrdersLoading,
    error,
  } = useGetOrdersQuery([
    REQUESTED_IMAGE_ANALYSIS_ATTRIBUTE,
    REQUESTED_PATHOLOGY_CONSULTATION_ATTRIBUTE,
    REQUESTED_SPECIAL_STAINS_ATTRIBUTE,
    REQUESTED_H_AND_E_ATTRIBUTE,
    IF_SLIDES_COUNT_ATTRIBUTE,
    SLIDES_COUNT_ATTRIBUTE,
  ]);
  useQueryError(error, FETCH_ORDERS_ERROR_MESSAGE);

  const filteredOrders = filterOrdersByTeam(rawOrders, [team]);

  const splittedOrders = useMemo(
    () => splitOrders(filteredOrders),
    [filteredOrders],
  );

  const selectedTabOrders = useMemo(
    () => getSelectedOrders(tab, splittedOrders, filteredOrders),
    [tab, splittedOrders],
  );

  const selectedTabSortedOrders = useMemo(
    () => sortArray(selectedTabOrders, ordersSorter, sort),
    [sort, selectedTabOrders],
  );

  return {
    selectedTabSortedOrders,
    isOrdersLoading,
  };
};

export const usePrepareChangeLog = (tab, orders) => {
  const {
    data: ordersHistory,
    error: getHistoryError,
    isLoading: isOrdersLoading,
  } = useGetOrdersHistoryQuery();

  useQueryError(getHistoryError, FETCH_ORDER_HISTORY_ERROR_MESSAGE);

  const filterEvents = (events) => {
    if (!events) return { archivedEvents: [], contributedEvents: [] };
    const archivedEvents = [];
    const contributedEvents = [];
    const previousStatus = {};

    events.forEach((event) => {
      const { uuid } = event;
      if (!previousStatus[uuid]) {
        previousStatus[uuid] = {
          isArchived: false,
          isContributed: false,
        };
      }
      const relatedOrder = orders.find((o) => o.uuid === uuid);
      if (event.is_storage_archived && !previousStatus[uuid].isArchived) {
        archivedEvents.push({
          date: event.history_date,
          action: 'delete',
          orderedBy: `${event.history_user.first_name} ${event.history_user.last_name}`,
          used: relatedOrder?.slides_data_used,
          projectName: relatedOrder?.project?.name,
          created: event.created,
          slidesCount: relatedOrder?.slides_count,
          ifSlidesCount: relatedOrder?.if_slides_count,
          order: relatedOrder,
        });
      }
      if (event.pathology_map_consent && !previousStatus[uuid].isContributed) {
        contributedEvents.push({
          date: event.history_date,
          action: 'contribute',
          orderedBy: `${event.history_user.first_name} ${event.history_user.last_name}`,
          used: relatedOrder?.slides_data_used,
          projectName: relatedOrder?.project?.name,
          created: event.created,
          slidesCount: relatedOrder?.slides_count,
          ifSlidesCount: relatedOrder?.if_slides_count,
          order: relatedOrder,
        });
      }

      previousStatus[uuid].isArchived = event.is_storage_archived;
      previousStatus[uuid].isContributed = event.pathology_map_consent;
    });

    return {
      archivedEvents: archivedEvents.sort(sorterForChangelog),
      contributedEvents: contributedEvents.sort(sorterForChangelog),
    };
  };

  const currentOrdersHistory = ordersHistory?.filter((order) =>
    orders.find((o) => o.uuid === order.uuid),
  );
  const { archivedEvents, contributedEvents } =
    filterEvents(currentOrdersHistory);

  const exportedEvents = orders
    ?.filter((order) => !!order.exportedOrdersHistory?.length)
    .map((order) => {
      const exportedOrder = order.exportedOrdersHistory[0];

      return {
        date: exportedOrder.created,
        action: 'export',
        orderedBy: `${exportedOrder.requested_by.first_name} ${exportedOrder.requested_by.last_name}`,
        used: order.slides_data_used,
        projectName: order.project?.name,
        created: exportedOrder.created,
        slidesCount: order.slides_count,
        ifSlidesCount: order.if_slides_count,
        order,
      };
    })
    .sort(sorterForChangelog);

  const allEvents = [
    ...archivedEvents,
    ...contributedEvents,
    ...exportedEvents,
  ].sort(sorterForChangelog);

  const selectedTabOrders = useMemo(
    () =>
      getSelectedHistoryTabs(tab, {
        allEvents,
        archivedEvents,
        contributedEvents,
        exportedEvents,
      }),
    [tab, allEvents],
  );

  return {
    selectedTabOrders,
    isOrdersLoading,
  };
};

export const useAnnotationsInfiniteScroll = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [getAllAnnotationsPaginated] = useLazyGetAllAnnotationsPaginatedQuery();

  const fetchData = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const response = await getAllAnnotationsPaginated({ page });
      const data = response.data?.results || [];

      if (data.length > 0) {
        setItems((prevItems) =>
          removeDuplicatesByUuid([...prevItems, ...data]),
        );
        setPage((prevPage) => prevPage + 1);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [page, loading, hasMore]);

  return { items, loading, hasMore, fetchData };
};
